import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { SectionHeading } from "@/components/site/SectionHeading";
import { jsonLdScript } from "@/lib/jsonld";
import { useState } from "react";

const PARTNERS = [
  { name: "National Board of Revenue (NBR)", url: "https://nbr.gov.bd/", desc: "Official tax & VAT authority of Bangladesh." },
  { name: "NBR e-Return Portal", url: "https://etaxnbr.gov.bd/", desc: "Government portal for online income tax return filing." },
  { name: "NBR VAT Online", url: "https://vat.gov.bd/", desc: "Official online VAT registration & Mushak filing." },
  { name: "Bangladesh Bank", url: "https://www.bb.org.bd/", desc: "Central bank — foreign remittance and tax circulars." },
  { name: "Office of the Registrar of Joint Stock Companies (RJSC)", url: "https://roc.gov.bd/", desc: "Company incorporation & annual return filing." },
  { name: "Bangladesh Investment Development Authority (BIDA)", url: "https://bida.gov.bd/", desc: "Investment registration & permits." },
  { name: "Bangladesh Government Portal", url: "https://bangladesh.gov.bd/", desc: "Central directory of government services." },
  { name: "Dhaka North City Corporation", url: "https://dncc.gov.bd/", desc: "Trade license & municipal services (DNCC)." },
  { name: "Dhaka South City Corporation", url: "https://dscc.gov.bd/", desc: "Trade license & municipal services (DSCC)." },
];

const EMBED_BADGE = `<a href="https://onlinetaxvat.com/" target="_blank" rel="noopener" title="Online Tax VAT Bangladesh">
  <img src="https://onlinetaxvat.com/logo.png" alt="Online Tax VAT Bangladesh" width="120" height="120" loading="lazy" />
</a>`;

const EMBED_IFRAME = `<iframe src="https://onlinetaxvat.com/partners?embed=1" width="240" height="160" style="border:0" loading="lazy" title="Online Tax VAT Badge"></iframe>`;

export const Route = createFileRoute("/partners")({
  head: () => ({
    meta: [
      { title: "Partners & Brand Assets | Online Tax VAT" },
      { name: "description", content: "Official Bangladesh tax & VAT government resources, partner directory, and embeddable Online Tax VAT badge for bloggers and affiliates." },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: "Partners & Brand Assets | Online Tax VAT" },
      { property: "og:description", content: "Authoritative Bangladesh tax resources and embeddable brand badge." },
    ],
    links: [{ rel: "canonical", href: "https://onlinetaxvat.com/partners" }],
    scripts: [
      jsonLdScript({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Partners & Brand Assets",
        url: "https://onlinetaxvat.com/partners",
        about: PARTNERS.map((p) => ({ "@type": "Organization", name: p.name, url: p.url })),
      }),
    ],
  }),
  component: PartnersPage,
});

function CopyBox({ label, code }: { label: string; code: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium">{label}</span>
        <button
          type="button"
          onClick={onCopy}
          className="text-xs px-3 py-1 rounded-md bg-primary text-primary-foreground hover:opacity-90"
        >
          {copied ? "Copied ✓" : "Copy"}
        </button>
      </div>
      <pre className="text-xs overflow-x-auto whitespace-pre-wrap break-all bg-muted/40 p-3 rounded">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function PartnersPage() {
  return (
    <SiteShell>
      <section className="container mx-auto px-4 py-12 max-w-5xl">
        <SectionHeading
          title="Partners & Brand Assets"
          description="Official Bangladesh tax & VAT resources and embeddable badge for partners."
        />

        <article className="mt-8 space-y-4">
          <h2 className="text-xl font-semibold">Authoritative Government Resources</h2>
          <p className="text-muted-foreground text-sm">
            Online Tax VAT works alongside the following official Bangladesh government
            portals. Verify any tax requirement directly with the relevant authority.
          </p>
          <ul className="grid gap-3 sm:grid-cols-2">
            {PARTNERS.map((p) => (
              <li key={p.url} className="rounded-lg border border-border p-4 hover:border-primary/40 transition-colors">
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener external"
                  className="font-medium text-primary hover:underline"
                >
                  {p.name} ↗
                </a>
                <p className="text-xs text-muted-foreground mt-1">{p.desc}</p>
              </li>
            ))}
          </ul>
        </article>

        <article className="mt-12 space-y-4">
          <h2 className="text-xl font-semibold">Share & Embed Our Badge</h2>
          <p className="text-muted-foreground text-sm">
            Bloggers, accountants and partner sites can embed the Online Tax VAT badge.
            Copy either snippet below and paste it into your site's HTML.
          </p>
          <CopyBox label="HTML badge (recommended)" code={EMBED_BADGE} />
          <CopyBox label="iframe embed" code={EMBED_IFRAME} />
          <div className="rounded-lg border border-border p-4 flex items-center gap-4">
            <a href="https://onlinetaxvat.com/" target="_blank" rel="noopener">
              <img src="/logo.png" alt="Online Tax VAT Bangladesh" width={96} height={96} loading="lazy" />
            </a>
            <div className="text-sm text-muted-foreground">
              Preview of the badge as it will appear on your site, linking back to{" "}
              <a href="https://onlinetaxvat.com/" className="text-primary hover:underline">
                onlinetaxvat.com
              </a>
              .
            </div>
          </div>
        </article>
      </section>
    </SiteShell>
  );
}
